import React from "react";
import { Info } from "lucide-react";

export interface DataSourceTable {
    name: string;
    description: React.ReactNode;
}

export interface DataSourceSection {
    title: string;
    items: { name: string; description: React.ReactNode }[];
}

interface DataSourcePanelProps {
    /** Short summary shown above the collapsible technical details. */
    summary: React.ReactNode;
    /**
     * Sections of technical details (e.g. APIs called, tables read, derivation notes).
     * Each section renders as a heading followed by a list.
     */
    sections?: DataSourceSection[];
    /** Optional badge label; defaults to "PRODUCTION DATA". Pass null/empty to hide. */
    badge?: string | null;
    /** Optional badge background colour; defaults to red. */
    badgeColor?: string;
    /** Margin-top on the panel; defaults to 24. */
    marginTop?: number;
}

const DataSourcePanel: React.FC<DataSourcePanelProps> = ({
    summary,
    sections = [],
    badge = "PRODUCTION DATA",
    badgeColor = "var(--accent-red, #dc2626)",
    marginTop = 24,
}) => {
    return (
        <div
            style={{
                marginTop,
                padding: "16px 20px",
                background: "var(--bg-secondary)",
                border: "1px solid var(--border-color)",
                borderRadius: 8,
                fontSize: "0.85rem",
                color: "var(--text-secondary)",
                lineHeight: 1.5,
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 8,
                    fontWeight: 600,
                    color: "var(--text-primary)",
                }}
            >
                <Info size={16} />
                <span>Data Sources</span>
                {badge && (
                    <span
                        style={{
                            marginLeft: 8,
                            padding: "2px 8px",
                            background: badgeColor,
                            color: "#fff",
                            borderRadius: 4,
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            letterSpacing: 0.5,
                        }}
                    >
                        {badge}
                    </span>
                )}
            </div>
            <div style={{ margin: "0 0 8px 0" }}>{summary}</div>

            {sections.length > 0 && (
                <details>
                    <summary
                        style={{
                            cursor: "pointer",
                            color: "var(--accent-blue)",
                            userSelect: "none",
                        }}
                    >
                        Show technical details
                    </summary>
                    <div
                        style={{
                            marginTop: 12,
                            padding: "12px 14px",
                            background: "var(--bg-primary)",
                            borderRadius: 6,
                            border: "1px solid var(--border-color)",
                        }}
                    >
                        {sections.map((section, idx) => (
                            <React.Fragment key={idx}>
                                <p
                                    style={{
                                        margin:
                                            idx === 0
                                                ? "0 0 10px 0"
                                                : "12px 0 10px 0",
                                        fontWeight: 600,
                                        color: "var(--text-primary)",
                                    }}
                                >
                                    {section.title}
                                </p>
                                <ul style={{ margin: "0 0 0 18px", padding: 0 }}>
                                    {section.items.map((item, i) => (
                                        <li key={i}>
                                            <code>{item.name}</code>
                                            {" — "}
                                            {item.description}
                                        </li>
                                    ))}
                                </ul>
                            </React.Fragment>
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
};

export default DataSourcePanel;
